import { createFileRoute } from "@tanstack/react-router";
import {
  Typography,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Flex,
  Tag,
  App,
  Spin,
  Badge,
  Descriptions,
  Switch,
  theme,
} from "antd";
import { SaveOutlined, LockOutlined, CheckCircleOutlined, BellOutlined, SafetyCertificateOutlined, UndoOutlined, ThunderboltOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useQ_PageSettings_ProviderConfig } from "@/hooks/OrgSettings/useQ_PageSettings_ProviderConfig";
import { useQ_PageSettings_BrokerFees } from "@/hooks/OrgSettings/useQ_PageSettings_BrokerFees";
import { useM_PageSettings_ProviderConfigUpsert, type UseM_PageSettings_ProviderConfigUpsert_Body } from "@/hooks/OrgSettings/useM_PageSettings_ProviderConfigUpsert";
import { useM_PageSettings_BrokerFeesReplace } from "@/hooks/OrgSettings/useM_PageSettings_BrokerFeesReplace";
import { useM_PageSettings_CredentialsUpsert } from "@/hooks/OrgSettings/useM_PageSettings_CredentialsUpsert";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND",
  "OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

type BrokerFeeOverrideRow = { state: string | null; fee: number | null };

export const Route = createFileRoute(
  "/_protected/$organizationId/$flowId/settings/provider",
)({
  component: Page_Settings_Provider,
});

function Page_Settings_Provider() {
  const { token } = theme.useToken();
  const { organizationId, flowId } = Route.useParams();
  const { message } = App.useApp();
  const qProviderConfig = useQ_PageSettings_ProviderConfig({ organizationId, flowSlug: flowId });
  const qBrokerFees = useQ_PageSettings_BrokerFees({ organizationId, flowSlug: flowId });
  const mConfigUpsert = useM_PageSettings_ProviderConfigUpsert({ organizationId, flowSlug: flowId });
  const mBrokerFees = useM_PageSettings_BrokerFeesReplace({ organizationId, flowSlug: flowId });
  const mCredentials = useM_PageSettings_CredentialsUpsert({ organizationId, flowSlug: flowId });
  const [configForm] = Form.useForm();
  const [carrierForm] = Form.useForm();
  const [flowEnabled, setFlowEnabled] = useState(false);
  const [enabledStates, setEnabledStates] = useState<string[]>([]);
  const [brokerFeeDefault, setBrokerFeeDefault] = useState<number | null>(null);
  const [brokerFeeOverrides, setBrokerFeeOverrides] = useState<BrokerFeeOverrideRow[]>([]);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [isConfigDirty, setIsConfigDirty] = useState(false);
  const [isCarrierDirty, setIsCarrierDirty] = useState(false);

  useEffect(() => {
    if (qProviderConfig.config) {
      configForm.setFieldsValue({
        location_code: qProviderConfig.config.location_code,
        producer: qProviderConfig.config.producer,
        policy_type: qProviderConfig.config.policy_type,
        payment_method: qProviderConfig.config.payment_method,
        signing_preference: qProviderConfig.config.signing_preference,
      });
      setFlowEnabled(qProviderConfig.config.flow_enabled ?? false);
      setEnabledStates(qProviderConfig.config.enabled_states || []);
      setBrokerFeeDefault(
        qProviderConfig.config.broker_fee_default_cents == null
          ? null
          : qProviderConfig.config.broker_fee_default_cents / 100,
      );
      setEmailRecipients(qProviderConfig.config.notification_email_recipients || []);
      setIsConfigDirty(false);
    }
  }, [qProviderConfig.config, configForm]);

  useEffect(() => {
    if (qBrokerFees.query.data) {
      setBrokerFeeOverrides(
        qBrokerFees.query.data.map((row) => ({ state: row.state, fee: row.fee_cents / 100 })),
      );
    }
  }, [qBrokerFees.query.data]);

  const handleConfigSave = async () => {
    const values = configForm.getFieldsValue() as UseM_PageSettings_ProviderConfigUpsert_Body;
    await mConfigUpsert.mutation.mutateAsync({
      location_code: values.location_code,
      producer: values.producer,
      policy_type: values.policy_type,
      payment_method: values.payment_method,
      signing_preference: values.signing_preference,
      flow_enabled: flowEnabled,
      enabled_states: enabledStates,
      broker_fee_default_cents: brokerFeeDefault == null ? null : Math.round(brokerFeeDefault * 100),
      notification_email_recipients: emailRecipients,
    });
    await mBrokerFees.mutation.mutateAsync({
      fees: brokerFeeOverrides
        .filter((row): row is { state: string; fee: number } => !!row.state && row.fee != null)
        .map((row) => ({ state: row.state, fee_cents: Math.round(row.fee * 100) })),
    });
    message.success("Provider configuration saved");
  };

  const handleCarrierSave = async () => {
    const values = await carrierForm.validateFields() as { username: string; password: string };
    await mCredentials.mutation.mutateAsync({
      type: "carrier",
      fields: { username: values.username, password: values.password },
    });
    carrierForm.resetFields();
    setIsCarrierDirty(false);
    message.success("Carrier credentials saved");
  };

  const handleSave = async () => {
    try {
      if (isConfigDirty) await handleConfigSave();
      if (isCarrierDirty) await handleCarrierSave();
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Failed to save");
    }
  };

  const handleDiscard = () => {
    if (isConfigDirty && qProviderConfig.config) {
      configForm.setFieldsValue({
        location_code: qProviderConfig.config.location_code,
        producer: qProviderConfig.config.producer,
        policy_type: qProviderConfig.config.policy_type,
        payment_method: qProviderConfig.config.payment_method,
        signing_preference: qProviderConfig.config.signing_preference,
      });
      setFlowEnabled(qProviderConfig.config.flow_enabled ?? false);
      setEnabledStates(qProviderConfig.config.enabled_states || []);
      setBrokerFeeDefault(
        qProviderConfig.config.broker_fee_default_cents == null
          ? null
          : qProviderConfig.config.broker_fee_default_cents / 100,
      );
      setBrokerFeeOverrides(
        qBrokerFees.fees.map((row) => ({ state: row.state, fee: row.fee_cents / 100 })),
      );
      setEmailRecipients(qProviderConfig.config.notification_email_recipients || []);
      setIsConfigDirty(false);
    }
    if (isCarrierDirty) {
      carrierForm.resetFields();
      setIsCarrierDirty(false);
    }
  };

  if (qProviderConfig.query.isLoading) {
    return (
      <Flex justify="center" style={{ padding: token.paddingXL }}>
        <Spin />
      </Flex>
    );
  }

  const carrierConfigured = !!qProviderConfig.config?.carrier_credentials_ref;
  const isDirty = isConfigDirty || isCarrierDirty;

  return (
    <Flex vertical gap={token.marginLG} style={{ paddingBottom: isDirty ? 64 : 0 }}>
      <Card title="Dairyland Configuration">
        <Form
          form={configForm}
          layout="vertical"
          onValuesChange={() => setIsConfigDirty(true)}
          initialValues={{
            policy_type: "Named Non-Owner",
            payment_method: "agency-sweep-cash",
            signing_preference: "paper-in-office",
          }}
        >
          <Flex align="center" justify="space-between" style={{ marginBottom: token.marginLG }}>
            <Flex vertical gap={4}>
              <Flex align="center" gap={token.marginXS}>
                <ThunderboltOutlined />
                <Typography.Title level={5} style={{ margin: 0 }}>Flow Enabled</Typography.Title>
              </Flex>
              <Typography.Text type="secondary">
                When disabled, tenant API calls will be rejected and the flow will not appear as available.
              </Typography.Text>
            </Flex>
            <Switch
              checked={flowEnabled}
              onChange={(checked) => { setFlowEnabled(checked); setIsConfigDirty(true); }}
            />
          </Flex>

          <Typography.Title level={5}>Provider Identity</Typography.Title>
          <Flex gap={token.marginMD} wrap="wrap">
            <Form.Item
              name="location_code"
              label="Location Code"
              rules={[{ required: true, message: "Location code is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Input placeholder="e.g. 10061277" />
            </Form.Item>
            <Form.Item
              name="producer"
              label="Producer"
              rules={[{ required: true, message: "Producer is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Input placeholder="e.g. Joal Ortiz" />
            </Form.Item>
          </Flex>
          <Flex gap={token.marginMD} wrap="wrap">
            <Form.Item name="policy_type" label="Policy Type" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="e.g. Named Non-Owner" />
            </Form.Item>
            <Form.Item name="payment_method" label="Payment Method" style={{ flex: 1, minWidth: 200 }}>
              <Select
                options={[{ value: "agency-sweep-cash", label: "Agency Sweep - Cash" }]}
                placeholder="Select payment method"
              />
            </Form.Item>
            <Form.Item name="signing_preference" label="Signing Preference" style={{ flex: 1, minWidth: 200 }}>
              <Select
                options={[
                  { value: "paper-in-office", label: "Paper (In-Office)" },
                  { value: "electronic-email", label: "Electronic - Email" },
                ]}
                placeholder="Select signing preference"
              />
            </Form.Item>
          </Flex>

          <Typography.Title level={5} style={{ marginTop: token.marginMD }}>
            Coverage & Automation
          </Typography.Title>
          <Form.Item label="Enabled States">
            <Select
              mode="multiple"
              value={enabledStates}
              onChange={(values) => { setEnabledStates(values); setIsConfigDirty(true); }}
              placeholder="Select states"
              showSearch
              options={US_STATES.map((s) => ({ value: s, label: s }))}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: token.marginMD }}>
            Broker Fee
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            Added on top of the carrier premium by the tenant checkout. A state override wins over
            the default; with neither set, no fee is applied. Overrides are kept even for disabled states.
          </Typography.Paragraph>
          <Form.Item label="Default Fee">
            <InputNumber
              prefix="$"
              min={0}
              max={10000}
              precision={2}
              value={brokerFeeDefault}
              onChange={(value) => { setBrokerFeeDefault(value); setIsConfigDirty(true); }}
              placeholder="No default fee"
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item label="State Overrides">
            <Flex vertical gap={token.marginXS}>
              {brokerFeeOverrides.map((row, index) => (
                <Flex key={row.state ?? `new-${index}`} gap={token.marginXS} align="center">
                  <Select
                    value={row.state}
                    onChange={(state) => {
                      setBrokerFeeOverrides((rows) => rows.map((r, i) => (i === index ? { ...r, state } : r)));
                      setIsConfigDirty(true);
                    }}
                    placeholder="State"
                    showSearch
                    options={US_STATES
                      .filter((s) => s === row.state || !brokerFeeOverrides.some((r) => r.state === s))
                      .map((s) => ({ value: s, label: s }))}
                    style={{ width: 120 }}
                  />
                  <InputNumber
                    prefix="$"
                    min={0}
                    max={10000}
                    precision={2}
                    value={row.fee}
                    onChange={(fee) => {
                      setBrokerFeeOverrides((rows) => rows.map((r, i) => (i === index ? { ...r, fee } : r)));
                      setIsConfigDirty(true);
                    }}
                    placeholder="Fee"
                    style={{ width: 160 }}
                  />
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setBrokerFeeOverrides((rows) => rows.filter((_, i) => i !== index));
                      setIsConfigDirty(true);
                    }}
                  />
                </Flex>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() => setBrokerFeeOverrides((rows) => [...rows, { state: null, fee: null }])}
                style={{ width: "fit-content" }}
              >
                Add state override
              </Button>
            </Flex>
          </Form.Item>
          <Typography.Title level={5} style={{ marginTop: token.marginMD }}>
            <Flex align="center" gap={token.marginXS}>
              <SafetyCertificateOutlined />
              Login Session
            </Flex>
          </Typography.Title>

          <Descriptions column={1} size="small" style={{ marginBottom: token.marginMD }}>
            <Descriptions.Item label="Cookie State">
              {qProviderConfig.config?.storage_state ? (
                <Typography.Text type="success">
                  Stored ({Math.round(JSON.stringify(qProviderConfig.config.storage_state).length / 1024)} KB)
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">No stored cookies</Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Title level={5} style={{ marginTop: token.marginMD }}>
            <Flex align="center" gap={token.marginXS}>
              <BellOutlined />
              Notifications
            </Flex>
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            Recipients are emailed when a policy is purchased, when the automation
            breaks, or when a bind is attempted on a risk the carrier declines.
            Routine carrier outcomes on a rate-check — declines, outstanding
            balances, unsupported states — are recorded but not emailed.
          </Typography.Paragraph>
          <Form.Item label="Email Recipients">
            <Select
              mode="tags"
              value={emailRecipients}
              onChange={(values) => { setEmailRecipients(values.filter((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))); setIsConfigDirty(true); }}
              placeholder="Type an email address and press Enter"
              tokenSeparators={[","]}
              style={{ width: "100%" }}
              notFoundContent={null}
            />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title={
          <Flex align="center" gap={token.marginSM}>
            <LockOutlined />
            <span>Carrier Credentials (Dairyland Login)</span>
            {carrierConfigured && (
              <Badge
                count={
                  <Flex align="center" gap={4}>
                    <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                    <Typography.Text type="success" style={{ fontSize: token.fontSizeSM }}>
                      Configured
                    </Typography.Text>
                  </Flex>
                }
              />
            )}
          </Flex>
        }
      >
        {carrierConfigured && (
          <Tag color="green" style={{ marginBottom: token.marginMD }}>
            Credentials are encrypted and stored. Re-enter to overwrite.
          </Tag>
        )}
        <Form
          form={carrierForm}
          layout="vertical"
          onValuesChange={() => setIsCarrierDirty(true)}
        >
          <Flex gap={token.marginMD} wrap="wrap">
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: "Username is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Input placeholder="Dairyland portal username" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Password is required" }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Input.Password placeholder="Dairyland portal password" />
            </Form.Item>
          </Flex>
        </Form>
      </Card>

      {isDirty && (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: token.colorBgElevated,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: `${token.paddingSM}px ${token.paddingLG}px`,
          boxShadow: token.boxShadowSecondary,
        }}>
          <Flex justify="space-between" align="center">
            <Typography.Text strong>Unsaved changes</Typography.Text>
            <Flex gap={token.marginSM}>
              <Button icon={<UndoOutlined />} onClick={handleDiscard}>Discard</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={mConfigUpsert.mutation.isPending || mBrokerFees.mutation.isPending || mCredentials.mutation.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </div>
      )}
    </Flex>
  );
}
